import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../../shared/components/pageHeader/PageHeaderApi"
import { StandardTabs } from "../../../../../../../shared/components/tabs/StandardTabs"
import { WalletsRefillManual } from "../components/WalletsRefillManual"
import { WalletsRefillAuto } from "../components/WalletsRefillAuto"
import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { fetchUserWalletsDetails, FetchUserWalletsDetailsResponse, WalletsRefill } from "../refillApi"
import { useParams } from "react-router-dom"
import { UserWallet } from "../refillTypes"
import { useLocationState } from "../../../../../../../core/router/routerHooks"
import { Suspense, useState } from "react"
import { LoadingSpinner } from "../../../../../../../shared/components/loading/LoadingSpinner"
import { QueryErrorBoundary } from "../../../../../../../core/api/components/QueryErrorBoundary"
import { useAppSelector } from "../../../../../../store/storeHooks"
import { selectEffectiveUserId } from "../../../../../../context/contextSelectors"

export type UserWalletsRefillState = Readonly<{
    wallet?: UserWallet
}>

function transformData(data: FetchUserWalletsDetailsResponse) {
    return data.wallet
}

type ContentProps = Readonly<{
    userId: number
    acquirerSetupId: number
    walletId: number
    wallet: UserWallet | undefined
}>

function UserWalletRefillContent({ userId, acquirerSetupId, walletId, wallet }: ContentProps) {
    const { l10n } = useAppLocalization()

    const walletDetails = useApiQuery(fetchUserWalletsDetails, {
        queryName: WalletsRefill.WALLET_REFILL_DETAILS,
        dependencies: {
            userId,
            acquirerSetupId,
            walletId,
        },
        enabled: !wallet?.paymentDetails.refill && Boolean(acquirerSetupId),
        select: transformData,
        suspense: true,
        useErrorBoundary: true,
    })

    const walletToUse = wallet ?? walletDetails.data

    const tabs = [
        {
            label: l10n.getString("user-wallets-refill-manual-top-up-nav-tab"),
            component: <WalletsRefillManual walletDetails={walletToUse!} />,
        },
        {
            label: l10n.getString("user-wallets-refill-automatic-top-up-nav-tab"),
            component: <WalletsRefillAuto walletDetails={walletToUse!} />,
        },
    ]

    return (
        <StandardTabs tabs={tabs} />
    )
}

export function UserWalletsRefill() {
    const { acquirerSetupId, walletId } = useParams<{ acquirerSetupId: string; walletId?: string }>()
    const { wallet: walletState } = useLocationState<UserWalletsRefillState>({ wallet: undefined })
    const [wallet, _setWallet] = useState(walletState)
    const userId = useAppSelector(selectEffectiveUserId) || 0

    const walletIdNumber: number = walletId !== undefined && !isNaN(Number(walletId)) ? Number(walletId) : 0

    const acquirerSetupIdNumber: number =
        acquirerSetupId !== undefined && !isNaN(Number(acquirerSetupId)) ? Number(acquirerSetupId) : 0

    return (
        <>
            <LocalizedStrict id="user-wallets-refill-title" attrs={{ title: true }}>
                <PageHeader page={Page.USER_WALLETS_REFILL} title="Refill wallet" />
            </LocalizedStrict>

            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <UserWalletRefillContent userId={userId} acquirerSetupId={acquirerSetupIdNumber} walletId={walletIdNumber} wallet={wallet} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
